import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  useGetApiCategoryTreeQuery,
  usePutApiArticlesByIdMutation,
} from '../../../redux/store/api/api';
import {
  selectContextArticle,
  selectRenameStructureElementDialogOpened,
  setContextCategory,
  setFocusedTreeItem,
  setRenameStructureElementDialogOpened,
} from '../../../redux/store/content/slice';
import { addMessage } from '../../../redux/store/layout/slice';
import CustomDialog from '../../dialogs/CustomDialog';
import Loader from '../../loader/Loader';

function RenameStructureElementDialog() {
  const dispatch = useAppDispatch();
  const { t: translation } = useTranslation();
  const dialogShow = useAppSelector(selectRenameStructureElementDialogOpened);
  const structureElement = useAppSelector(selectContextArticle);
  const [elementName, setElementName] = useState(
    structureElement.recentVersionTitle || '',
  );
  const [isValidName, setIsValidName] = useState(true);
  const [renameStructureElement, { isError, isLoading, error }] =
    usePutApiArticlesByIdMutation();
  const { refetch } = useGetApiCategoryTreeQuery();

  useEffect(() => {
    if (isError) {
      dispatch(
        addMessage({
          id: 'UpdateStructureElementError',
          variant: 'danger',
          messageKeyBody:
            error && 'data' in error ? error.data?.messageKey : 'unknownError',
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    setElementName(structureElement.recentVersionTitle || '');
    setIsValidName(true);
  }, [structureElement]);

  const handleRenameStructureElement = (): void => {
    if (elementName.trim().length === 0) {
      setIsValidName(false);
      return;
    }

    renameStructureElement({
      id: structureElement.id || '',
      article: { ...structureElement, recentVersionTitle: elementName.trim() },
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'UpdateStructureElementSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        dispatch(setRenameStructureElementDialogOpened(false));
        refetch();
        dispatch(setContextCategory({}));
        dispatch(setFocusedTreeItem(structureElement.id ?? ''));
      });
  };

  return (
    <CustomDialog
      titleId='RenameStructureElementDialog'
      show={dialogShow}
      closeFunction={() => {
        dispatch(setRenameStructureElementDialogOpened(false));
        dispatch(setContextCategory({}));
        setIsValidName(true);
      }}
      closeTitle={translation('cancel')}
      actionFunction={handleRenameStructureElement}
      actionTitle={translation('rename')}
      actionButtonDisabled={
        elementName.trim() === structureElement.recentVersionTitle || isLoading
      }
      dialogTitle={translation('renameStructureElement')}>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <p>{translation('fieldsAreRequiredLegend')}</p>
          <Form.Group className='mb-3' controlId='StructureElementName'>
            <Form.Label>{translation('structureElementName')}*</Form.Label>
            <Form.Control
              aria-describedby={
                isValidName ? undefined : 'StructureElementNameInputError'
              }
              isInvalid={!isValidName}
              required
              onChange={(e) => {
                setElementName(e.target.value);
                if (e.target.value.trim().length > 0) {
                  setIsValidName(true);
                }
              }}
              type='text'
              value={elementName}
            />
            <Form.Control.Feedback
              id='StructureElementNameInputError'
              type='invalid'>
              {translation('fieldNotEmpty')}
            </Form.Control.Feedback>
          </Form.Group>
        </>
      )}
    </CustomDialog>
  );
}

export default RenameStructureElementDialog;
